import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../../utils/GlobalAPI';
import AdminPageHeader from '../AdminPageHeader';
import EditModal from '../EditModal';
import { FaChalkboardTeacher, FaEdit, FaTrash } from 'react-icons/fa';

const emptyCourse = {
  title: '',
  desc: '',
  price: '',
  img: '',
  lessons: '',
  duration: '',
  level: '',
};

const AdminUpskill = () => {
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState(emptyCourse);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch courses from backend on mount
  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(API_ENDPOINTS.COURSES);
        if (!res.ok) throw new Error('Failed to fetch courses');
        const data = await res.json();
        setCourses(data);
      } catch (err) {
        setError(err.message || 'Error fetching courses');
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.COURSES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to add course');
      }
      const data = await res.json();
      setCourses([...courses, data.course]);
      setForm(emptyCourse);
    } catch (err) {
      setError(err.message || 'Error adding course');
    } finally {
      setLoading(false);
    }
  };


  // Edit state
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState(emptyCourse);

  // Start editing a course
  const handleEditClick = (idx) => {
    setEditIdx(idx);
    setEditForm(courses[idx]);
  };

  // Handle edit form change
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
  };

  // Save edited course
  const handleEditSave = async (id) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINTS.COURSES}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to edit course');
      }
      const data = await res.json();
      const updated = courses.map((c, i) => (i === editIdx ? data.course : c));
      setCourses(updated);
      setEditIdx(null);
    } catch (err) {
      setError(err.message || 'Error editing course');
    } finally {
      setLoading(false);
    }
  };

  // Cancel editing
  const handleEditCancel = () => {
    setEditIdx(null);
    setEditForm(emptyCourse);
  };

  const handleEditSaveWrapper = async () => {
    if (editIdx !== null && courses[editIdx]?._id) {
      await handleEditSave(courses[editIdx]._id);
    }
  };

  // Delete a course
  const handleDelete = async (id, idx) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINTS.COURSES}/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to delete course');
      }
      setCourses(courses.filter((_, i) => i !== idx));
    } catch (err) {
      setError(err.message || 'Error deleting course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-50/50 min-h-screen">
      <AdminPageHeader
        title="Upskill Courses Control Panel"
        subtitle="Manage and edit educational courses available on the platform"
        icon={<FaChalkboardTeacher className="w-6 h-6" />}
      />

      <div className="pt-32 p-6 max-w-7xl mx-auto space-y-6">
        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
            <div className="text-rose-600 font-semibold">{error}</div>
          </div>
        )}
        {loading && (
          <div className="bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-xl p-4">
            <div className="text-orange-600 font-medium">Processing request...</div>
          </div>
        )}

        {/* Add Course Form */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h2 className="text-lg font-bold text-slate-800 mb-6">Create New Course Listing</h2>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleAdd}>
            <input 
              name="title" 
              value={form.title} 
              onChange={handleChange} 
              placeholder="Course Title" 
              required 
              className="px-4 py-3 border border-slate-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 placeholder:text-slate-400 text-sm"
            />
            <input 
              name="desc" 
              value={form.desc} 
              onChange={handleChange} 
              placeholder="Course Description" 
              required 
              className="px-4 py-3 border border-slate-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 placeholder:text-slate-400 text-sm"
            />
            <input 
              name="price" 
              value={form.price} 
              onChange={handleChange} 
              placeholder="Collab Pricing / Tuition Fee" 
              required 
              className="px-4 py-3 border border-slate-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 placeholder:text-slate-400 text-sm"
            />
            <input 
              name="img" 
              value={form.img} 
              onChange={handleChange} 
              placeholder="Image Illustration URL" 
              required 
              className="px-4 py-3 border border-slate-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 placeholder:text-slate-400 text-sm"
            />
            <input 
              name="lessons" 
              value={form.lessons} 
              onChange={handleChange} 
              placeholder="Number of Lessons / Chapters" 
              required 
              className="px-4 py-3 border border-slate-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 placeholder:text-slate-400 text-sm"
            />
            <input 
              name="duration" 
              value={form.duration} 
              onChange={handleChange} 
              placeholder="Course Duration (e.g. 12 Hours)" 
              required 
              className="px-4 py-3 border border-slate-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 placeholder:text-slate-400 text-sm"
            />
            <input 
              name="level" 
              value={form.level} 
              onChange={handleChange} 
              placeholder="Skill Target Level (e.g. Beginner, Advanced)" 
              required 
              className="md:col-span-2 px-4 py-3 border border-slate-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 placeholder:text-slate-400 text-sm"
            />
            <button 
              type="submit" 
              className="col-span-1 md:col-span-2 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 hover:opacity-95 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-md text-sm"
            >
              Add New Course Listing
            </button>
          </form>
        </div>

        {/* Courses Table */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-600">Available Course Catalog</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Preview</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Course Info</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Price Details</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Syllabus details</th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {courses.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-16 text-slate-500">
                      <div className="flex flex-col items-center">
                        <div className="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mb-3 border border-slate-100">
                          <FaChalkboardTeacher className="w-6 h-6 text-slate-400" />
                        </div>
                        <p className="text-base font-semibold text-slate-700">No courses listed yet</p>
                        <p className="text-xs text-slate-400 mt-1">Add a course listing to display it here</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  courses.map((course, idx) => (
                    <tr key={course._id || idx} className="hover:bg-slate-50/40 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {course.img ? (
                          <img src={course.img} alt={course.title} className="w-16 h-10 object-cover rounded-xl border border-slate-100 shadow-sm" />
                        ) : (
                          <div className="w-16 h-10 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-center">
                            <FaChalkboardTeacher className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold text-slate-800 text-sm">{course.title}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5 max-w-[200px] truncate" title={course.desc}>{course.desc}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 font-bold">
                        <span className="bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                          {course.price}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-600 space-y-0.5">
                        <div>Lessons: <span className="text-slate-800 font-bold font-mono">{course.lessons}</span></div>
                        <div className="text-[11px] text-slate-400">Duration: <span className="text-slate-600 font-semibold">{course.duration}</span></div>
                        <div className="text-[11px] text-slate-400">Level: <span className="text-slate-600 font-semibold">{course.level}</span></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-1.5">
                          <button 
                            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg border border-transparent hover:border-slate-200 transition-all" 
                            onClick={() => handleEditClick(idx)}
                            title="Edit Course Details"
                          >
                            <FaEdit className="w-3.5 h-3.5" />
                          </button>
                          <button 
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg border border-transparent hover:border-rose-100 transition-all" 
                            onClick={() => handleDelete(course._id, idx)}
                            title="Remove Course"
                          >
                            <FaTrash className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Modal */}
        <EditModal
          isOpen={editIdx !== null}
          onClose={handleEditCancel}
          title={`Edit Course: ${editForm.title || 'Untitled'}`}
          onSave={handleEditSaveWrapper}
          saveLabel="Save Changes"
          isLoading={loading}
        >
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm font-semibold">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Image Illustration URL *</label>
              <input 
                name="img" 
                value={editForm.img} 
                onChange={handleEditChange} 
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 text-sm"
                required
              />
              {editForm.img && (
                <img src={editForm.img} alt="Preview" className="mt-3 w-full h-32 object-cover rounded-xl border border-gray-200 shadow-sm" />
              )}
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title *</label>
                <input 
                  name="title" 
                  value={editForm.title} 
                  onChange={handleEditChange} 
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Pricing *</label>
                <input 
                  name="price" 
                  value={editForm.price} 
                  onChange={handleEditChange} 
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 text-sm"
                  required
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
              <textarea 
                name="desc" 
                value={editForm.desc} 
                onChange={handleEditChange} 
                rows={3}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-transparent resize-none outline-none text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Lessons *</label>
              <input 
                name="lessons" 
                value={editForm.lessons} 
                onChange={handleEditChange} 
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Duration *</label>
              <input 
                name="duration" 
                value={editForm.duration} 
                onChange={handleEditChange} 
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 text-sm"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Level *</label>
              <input 
                name="level" 
                value={editForm.level} 
                onChange={handleEditChange} 
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 text-sm"
                required
              />
            </div>
          </div>
        </EditModal>
      </div>
    </div>
  );
};

export default AdminUpskill;
